<script setup lang="ts">
/**
 * B.42 ToastContainer —— 在 AppLayout 挂一次,渲染 useToast() items 队列。
 *
 * 位置:fixed 顶部居中(比右上角更显眼,用户不易错过),栈式堆叠。
 * 点击 × 手动 dismiss;auto-dismiss 由 useToast.push() 的 setTimeout 管。
 */
import { useToast } from '@/composables/useToast'

const toast = useToast()

function iconFor(kind: 'success' | 'error' | 'info'): string {
  switch (kind) {
    case 'success':
      return 'ti ti-circle-check'
    case 'error':
      return 'ti ti-alert-triangle'
    case 'info':
    default:
      return 'ti ti-info-circle'
  }
}
</script>

<template>
  <div class="toast-container" aria-live="polite">
    <transition-group name="toast">
      <div
        v-for="t in toast.items"
        :key="t.id"
        class="toast"
        :class="`toast-${t.kind}`"
        role="status"
      >
        <i :class="iconFor(t.kind)" aria-hidden="true"></i>
        <span class="toast-msg">{{ t.message }}</span>
        <button
          type="button"
          class="toast-x"
          aria-label="关闭"
          @click="toast.dismiss(t.id)"
        >
          <i class="ti ti-x" aria-hidden="true"></i>
        </button>
      </div>
    </transition-group>
  </div>
</template>

<style scoped>
.toast-container {
  position: fixed;
  top: 56px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 200;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  pointer-events: none;
  /* container 不接 click,只 toast 自己接 */
}
.toast {
  pointer-events: auto;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 8px 8px 12px;
  background: var(--color-background-primary);
  border: 0.5px solid var(--color-border-tertiary);
  border-left-width: 3px;
  border-radius: var(--border-radius-md);
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
  min-width: 220px;
  max-width: 360px;
  font-size: var(--fs-control);
}
.toast-success {
  border-left-color: var(--color-text-success);
  background: var(--color-background-success);
}
.toast-success > i:first-child {
  color: var(--color-text-success);
}
.toast-error {
  border-left-color: var(--color-text-danger);
  background: var(--color-background-danger);
}
.toast-error > i:first-child {
  color: var(--color-text-danger);
}
.toast-info {
  border-left-color: var(--color-text-info);
  background: var(--color-background-info);
}
.toast-info > i:first-child {
  color: var(--color-text-info);
}
.toast > i:first-child {
  font-size: var(--fs-page);
  flex-shrink: 0;
}
.toast-msg {
  flex: 1;
  color: var(--color-text-primary);
  line-height: 1.4;
  word-break: break-word;
  white-space: pre-line;
}
.toast-x {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  padding: 0;
  border: none;
  border-radius: var(--border-radius-sm);
  background: transparent;
  color: var(--color-text-tertiary);
  cursor: pointer;
  flex-shrink: 0;
  font-family: inherit;
}
.toast-x:hover {
  background: var(--color-background-secondary);
  color: var(--color-text-primary);
}
.toast-x i {
  font-size: var(--fs-item);
}

/* 进出场动画 */
.toast-enter-active,
.toast-leave-active {
  transition: all 0.2s ease;
}
.toast-enter-from {
  opacity: 0;
  transform: translateY(-12px);
}
.toast-leave-to {
  opacity: 0;
  transform: translateY(-12px);
}
</style>
